import { FC, useState, MouseEvent, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import * as Yup from "yup";
import { Formik, FormikErrors, FormikHelpers } from "formik";
import Button from "@shared/components/button/Button";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button/types";
import { Checkbox } from "@shared/components/checkbox";
import { CHECKBOX_SIZE } from "@shared/components/checkbox/types";
import Tag from "@shared/components/tag/Tag";
import {
  TAG_COLOR,
  TAG_SIZE,
  TAG_STYLE,
  TAG_TYPE,
} from "@shared/components/tag";
import CheckCircleIcon from "@svg/new-design/check-circle";
import AiIcon from "@svg/certificate/ai";
import { detailList, langList, recommendedList, toneList } from "./constant";
import { certificateDetail } from "@types";
import SettingItem from "./setting-item";
import TranslateLanguageIcon from "@svg/new-design/translate-language";
import ChatIcon from "@svg/new-design/chat";
import TrafficIcon from "@svg/new-design/traffic";
import classNames from "classnames";
import {
  CERTIFICATE_DETAIl,
  CERTIFICATE_LANG,
  CERTIFICATE_TONE,
} from "enums/certificate";

interface Props {
  createPost: (description: string) => void;
  defaultValue: string;
  certificateLang: CERTIFICATE_LANG;
  persianTitle: string;
  englishTitle: string;
  withStickyBtn?: boolean;
}

const LinkedinConfig: FC<Props> = ({
  createPost,
  defaultValue,
  persianTitle,
  englishTitle,
  certificateLang,
  withStickyBtn,
}) => {
  const postInitial = {
    description: defaultValue,
    accept: false,
  };
  const [loading, setLoading] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimersRef = useRef<number[]>([]);
  const [setting, setSetting] = useState<{
    tone: CERTIFICATE_TONE | null;
    lang: CERTIFICATE_LANG | null;
    detail: certificateDetail;
  }>({
    tone: CERTIFICATE_TONE.FORMAL,
    lang: certificateLang,
    detail: { emoji: null, title: CERTIFICATE_DETAIl.WITH_COURSE_TITLE },
  });
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  const submit = (
    value: { description: string; accept: boolean },
    actions: FormikHelpers<{ description: string; accept: boolean }>
  ) => {
    createPost(value.description);
    actions.setSubmitting(false);
  };
  const folderSchema = Yup.object().shape({
    description: Yup.string()
      .required("انتخاب متن الزامی است")
      .test("emptyName", "انتخاب متن الزامی است", (value) => {
        if (value?.trim() === "") return false;
        return true;
      }),
    accept: Yup.boolean().required("لطفا صحت اطلاعات را تأیید کنید."),
  });

  const handleSetting = (name: "tone" | "lang" | "detail", value: string) => {
    if (name === "detail") {
      const { title, emoji } = setting.detail;
      if (value === CERTIFICATE_DETAIl.WITH_EMOJI) {
        setSetting({
          ...setting,
          detail: {
            title: title,
            emoji: emoji === value ? null : value,
          },
        });
      } else {
        setSetting({
          ...setting,
          detail: {
            emoji: emoji,
            title:
              title === value
                ? null
                : (value as CERTIFICATE_DETAIl.WITH_COURSE_TITLE | null),
          },
        });
      }
    } else {
      setSetting({ ...setting, [name]: value });
    }
  };
  const handleGenerateText = async (
    e: MouseEvent<HTMLButtonElement>,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<{
      description: string;
      accept: boolean;
    }>>
  ) => {
    e.preventDefault();
    setLoading(true);

    typingTimersRef.current.forEach((id) => window.clearTimeout(id));
    typingTimersRef.current = [];
    const { tone, lang, detail } = setting;
    let text: {
      sentence: string;
      tone: string;
      course_title: boolean;
      lang: string;
      using_emoji: boolean;
    }[];

    text =
      recommendedList[lang || certificateLang][tone || CERTIFICATE_TONE.FORMAL][
        detail.emoji === CERTIFICATE_DETAIl.WITH_EMOJI
          ? CERTIFICATE_DETAIl.WITH_EMOJI
          : ""
      ];

    const finalText = text.filter(
      (tItem) =>
        tItem.course_title ===
        (detail.title === CERTIFICATE_DETAIl.WITH_COURSE_TITLE)
    );

    const random = Math.abs(Math.round(Math.random() * finalText.length - 1));

    const generateText = (
      finalText[random] as {
        sentence: string;
        tone: string;
        course_title: boolean;
        lang: string;
        using_emoji: boolean;
      }
    ).sentence;

    const generateTextWithTitle = generateText.replace(
      /\{COURSE_TITLE}/g,
      ` ${
        lang === CERTIFICATE_LANG.EN
          ? `📚 ${englishTitle} 📚`
          : `📚 ${persianTitle} 📚`
      } `
    );

    if (textAreaRef.current) {
      let value = "";
      await setFieldValue("description", "");
      const chars = generateTextWithTitle.split("");
      const lastIndex = chars.length - 1;
      chars.forEach((char, index) => {
        const timerId = window.setTimeout(async () => {
          value += char;
          await setFieldValue("description", value);
          if (index === lastIndex) {
            setLoading(false);
          }
        }, index * 40);
        typingTimersRef.current.push(timerId);
      });
    } else {
      await setFieldValue("description", generateTextWithTitle);
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={postInitial}
      validationSchema={() => folderSchema}
      onSubmit={(values, actions) => {
        submit(values, actions);
      }}
      enableReinitialize={true}
      validateOnChange={true}
    >
      {({
        errors,
        handleChange,
        isSubmitting,
        isValid,
        values,
        setFieldValue,
        handleSubmit,
      }) => {
        return (
          <form
            className={classNames(
              " flex flex-col  justify-between mt-4     fds-sm:mt-6",
              withStickyBtn && "pb-10"
            )}
          >
            <div className="bg-fds-primary p-3  rounded-12 border border-dashed border-fds-brand-light ">
              <div className="flex gap-x-5    gap-y-6  fds-sm:flex-row flex-col">
                <SettingItem
                  list={langList}
                  name="lang"
                  handleSetting={handleSetting}
                  id={setting.lang}
                  icon={
                    <TranslateLanguageIcon className="text-fds-icon-tertiary fds-ms:h-5    fds-sm:w-5    h-4     w-4" />
                  }
                  title="  به چه زبانی نوشته شود؟"
                />
                <SettingItem
                  list={toneList}
                  name="tone"
                  handleSetting={handleSetting}
                  id={setting.tone}
                  icon={
                    <ChatIcon className="text-fds-icon-tertiary fds-ms:h-5    fds-sm:w-5    h-4     w-4" />
                  }
                  title="لحن متن چگونه باشد؟"
                />
              </div>

              <div className="fds-sm:mt-5    mt-6">
                <p className="text-fds-secondary body-xxsm mb-1">
                  <span className="body-sm">جزییات متن</span> (می‌توانی بیشتر از
                  یک گزینه انتخاب کنی)
                </p>

                <div className="flex gap-2">
                  {detailList.map((tList, index) => (
                    <Tag
                      onClick={(e) => {
                        e.preventDefault();
                        handleSetting("detail", tList.id);
                      }}
                      key={index}
                      style={
                        setting.detail[
                          tList.name as keyof certificateDetail
                        ] === tList.id
                          ? TAG_STYLE.FILL
                          : TAG_STYLE.OUTLINE
                      }
                      startIcon={
                        setting.detail[
                          tList.name as keyof certificateDetail
                        ] === tList.id ? (
                          <CheckCircleIcon fill="#fff" />
                        ) : undefined
                      }
                      showTagClose={
                        setting.detail[
                          tList.name as keyof certificateDetail
                        ] === tList.id
                      }
                      color={TAG_COLOR.DEFAULT}
                      type={TAG_TYPE.ROUNDED}
                      size={TAG_SIZE.MEDIUM}
                    >
                      {tList.title}
                    </Tag>
                  ))}
                </div>
              </div>
              <Button
                startIcon={
                  !loading ? (
                    <AiIcon className="text-fds-icon-brand ml-1" />
                  ) : undefined
                }
                endIcon={
                  loading ? (
                    <TrafficIcon className="text-fds-icon-brand mr-1 animate-spin" />
                  ) : undefined
                }
                disabled={loading}
                variant={BUTTON_VARIANT.OUTLINE}
                color={BUTTON_COLOR.PRIMARY}
                onClick={(e) => handleGenerateText(e, setFieldValue)}
                size={BUTTON_SIZE.MEDIUM}
                className="w-full disabled:border-fds-brand disabled:text-fds-brand fds-sm:mt-5    mt-6  mb-3   "
              >
                {loading ? "در حال ساخت متن " : "ساخت متن توضیحات"}
              </Button>
              <div className="p-3  w-full h-full  bg-fds-canvas-primary">
                <p className="fds-sm:title-md-medium title-sm fds-sm:mb-1.5  mb-1">
                  توضیحات پست لینکدین
                </p>
                <textarea
                  className="w-full fds-sm:p-2  p-1.5  border rounded-6 body-sm "
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  autoFocus
                  rows={3}
                  dir={"auto"}
                  ref={textAreaRef}
                  placeholder="متن مورد نظر شما یا ساخت متن آماده با کمک مدل AI..."
                />
              </div>
            </div>
            <div className="py-4    ">
              <Checkbox
                id="accept"
                size={CHECKBOX_SIZE.LARGE}
                selected={values.accept ? "checked" : "unChecked"}
                handleOnChange={(id, value: boolean) => {
                  setFieldValue(id, value);
                }}
                checkboxClassName="hidden fds-sm:flex"
                label="صحت اطلاعات درج شده در گواهینامه را تأیید می‌کنم."
              />
              <Checkbox
                id="accept"
                size={CHECKBOX_SIZE.MEDIUM}
                selected={values.accept ? "checked" : "unChecked"}
                handleOnChange={(id, value: boolean) => {
                  setFieldValue(id, value);
                }}
                checkboxClassName="flex fds-sm:hidden"
                label="صحت اطلاعات درج شده در گواهینامه را تأیید می‌کنم."
              />
            </div>

            <Button
              variant={BUTTON_VARIANT.FILL}
              color={BUTTON_COLOR.PRIMARY}
              disabled={isSubmitting || !isValid || !values.accept}
              size={BUTTON_SIZE.XLARGE}
              className={classNames(
                withStickyBtn ? "fds-sm:flex hidden" : "",
                " w-full "
              )}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              تأیید و اشتراک‌گذاری
            </Button>

            {withStickyBtn && isClient
              ? createPortal(
                  <div className="py-3  block fds-sm:hidden px-4     fixed bottom-0 inset-x-0 bg-fds-primary shadow-primary-xlg z-[1200]">
                    <Button
                      variant={BUTTON_VARIANT.FILL}
                      color={BUTTON_COLOR.PRIMARY}
                      disabled={isSubmitting || !isValid || !values.accept}
                      size={BUTTON_SIZE.LARGE}
                      className="w-full "
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit();
                      }}
                    >
                      تأیید و اشتراک‌گذاری
                    </Button>
                  </div>,
                  document?.body || null
                )
              : null}
          </form>
        );
      }}
    </Formik>
  );
};

export default LinkedinConfig;
